import React from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Table, Tag } from 'antd';
import Modal from '~/modules/components/global/modal';
import Button from '~/modules/components/global/button';
import EmptyState from '~/modules/components/global/empty-state';
import {
	TableStyles
} from '~/modules/components/share-feedback/share-feedback-report/share-feedback-report.css';
import ToastService from '~/modules/services/toast-service';
import WebhookEndpointService from '~/modules/services/webhook-endpoint-service';
import { captureError } from '../../../pixwel/exception-handler.factory';

const formatDate = (timestamp) => {
	if(!timestamp) {
		return '—';
	}
	return new Date(timestamp * 1000).toLocaleString();
};

const STATUS_COLORS = {
	succeeded: 'green',
	failed: 'red',
	pending_retry: 'orange',
	pending: 'blue'
};

const WebhookDeliveriesModal = ({ endpoint, onClose }) => {
	const queryClient = useQueryClient();
	const endpointId = endpoint?._id;
	const queryKey = ['webhookDeliveries', endpointId];

	const { data: deliveries = [], isLoading } = useQuery(
		queryKey,
		() => WebhookEndpointService.deliveries(endpointId),
		{ enabled: !!endpointId, refetchOnWindowFocus: false }
	);

	const { mutateAsync: retry } = useMutation(
		(id) => WebhookEndpointService.retryDelivery(id),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(queryKey);
				ToastService.create('success', 'Delivery re-queued');
			},
			onError: (err) => {
				ToastService.create('error', err?.response?.data?.error || 'Failed to retry delivery');
				captureError(err, `Error retrying webhook delivery: ${err.message}`);
			}
		}
	);

	const columns = [
		{ title: 'Event', dataIndex: 'eventType', key: 'eventType', ellipsis: true },
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (status) => <Tag color={STATUS_COLORS[status] || 'default'}>{status}</Tag>
		},
		{ title: 'Attempts', dataIndex: 'attempts', key: 'attempts', width: 90 },
		{
			title: 'HTTP',
			dataIndex: 'responseStatus',
			key: 'responseStatus',
			width: 80,
			render: (code) => code || '—'
		},
		{ title: 'Last attempt', dataIndex: 'lastAttemptAt', key: 'lastAttemptAt', render: formatDate },
		{
			title: 'Action',
			key: 'action',
			width: 120,
			render: (_value, row) => (
				<Button
					variant="secondary"
					data-testid="webhook-delivery-retry-button"
					onClick={() => retry(row._id)}
				>
					Resend
				</Button>
			)
		}
	];

	return (
		<Modal
			isOpen={!!endpoint}
			contentLabel="Webhook deliveries"
			size="lg"
			onRequestClose={onClose}
		>
			<Modal.Header>
				<h3>Recent deliveries</h3>
			</Modal.Header>
			<Modal.Body>
				<p style={{ wordBreak: 'break-all' }}><code>{endpoint?.url}</code></p>
				<TableStyles>
					<Table
						columns={columns}
						dataSource={deliveries.map((d) => ({ ...d, key: d._id }))}
						pagination={false}
						loading={isLoading}
						locale={{ emptyText: <EmptyState message="No deliveries yet" /> }}
					/>
				</TableStyles>
			</Modal.Body>
			<Modal.Footer>
				<Button onClick={onClose} data-testid="webhook-deliveries-close-button">
					Close
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

WebhookDeliveriesModal.propTypes = {
	endpoint: PropTypes.shape({
		_id: PropTypes.string,
		url: PropTypes.string
	}),
	onClose: PropTypes.func.isRequired
};

export default WebhookDeliveriesModal;
